import React, { useCallback, useRef } from 'react';
import { Search } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';
import { libelle, referenceLisible } from '../../lib/libelles';
import { progressOf } from '../../lib/orderProgress';
import { ETAT_VARIANTE, jourLisible, libre, pourcentAffecte } from './outils';

/**
 * Liste compacte des commandes: une ligne par commande, jamais de tableau
 * large. Flèches haut/bas: passer d'une commande à la suivante.
 */
export const DispatchOrderList = ({
  orders,
  selectedId,
  onSelect,
  volumeCell,
  uniteCourte,
  recherche,
  onRecherche,
}) => {
  const liste = useRef(null);

  const surTouche = useCallback(
    (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      if (orders.length === 0) return;
      event.preventDefault();
      const index = orders.findIndex((order) => order.id === selectedId);
      const suivant =
        event.key === 'ArrowDown'
          ? Math.min(orders.length - 1, index + 1)
          : Math.max(0, index === -1 ? 0 : index - 1);
      const cible = orders[suivant];
      if (!cible) return;
      onSelect(cible, { clavier: true });
      // Le focus suit la sélection, la ligne reste visible dans la liste.
      requestAnimationFrame(() => {
        const ligne = liste.current?.querySelector(`[data-order-id="${cible.id}"]`);
        if (ligne) {
          ligne.focus({ preventScroll: true });
          ligne.scrollIntoView?.({ block: 'nearest' });
        }
      });
    },
    [orders, selectedId, onSelect],
  );

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="search"
          value={recherche}
          onChange={(event) => onRecherche(event.target.value)}
          placeholder="Rechercher une commande, un client, un lieu"
          aria-label="Rechercher une commande"
          className="h-9 pl-8 text-sm"
        />
      </div>
      {orders.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Aucune commande ne correspond à cette recherche.
        </p>
      ) : (
        <ul
          ref={liste}
          role="listbox"
          aria-label="Commandes à répartir"
          onKeyDown={surTouche}
          className="divide-y divide-border overflow-hidden rounded-lg border border-border"
        >
          {orders.map((order, index) => {
            const progress = progressOf(order);
            const choisie = order.id === selectedId;
            const pct = pourcentAffecte(progress);
            const actif = choisie || (!selectedId && index === 0);
            return (
              <li
                key={order.id}
                role="option"
                aria-selected={choisie}
                tabIndex={actif ? 0 : -1}
                data-order-id={order.id}
                data-testid="dispatch-order-row"
                onClick={() => onSelect(order)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelect(order);
                  }
                }}
                className={cn(
                  'relative cursor-pointer space-y-1.5 px-3 py-2.5 outline-none transition-colors',
                  'hover:bg-muted/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
                  choisie && 'bg-primary/10 before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-primary',
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-sm font-semibold text-foreground">
                    {referenceLisible(order.reference_number)}
                  </span>
                  <Badge
                    variant={ETAT_VARIANTE[progress.state] || 'muted'}
                    className="shrink-0 whitespace-nowrap"
                  >
                    {libelle('etat_commande', progress.state)}
                  </Badge>
                </div>
                <p className="truncate text-xs text-muted-foreground">
                  {libre(order.client_name, 'Client non renseigné')} ·{' '}
                  {libre(order.delivery_location, 'Lieu non précisé')} ·{' '}
                  {jourLisible(order.requested_delivery_date)}
                </p>
                <div className="flex items-center gap-2">
                  <div
                    className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted"
                    role="progressbar"
                    aria-label="Part affectée de la commande"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={pct}
                  >
                    <div className="h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="w-10 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                    {pct} %
                  </span>
                </div>
                <p className="flex items-baseline justify-between gap-2 text-xs tabular-nums">
                  <span className="min-w-0 truncate text-muted-foreground">
                    {volumeCell(progress.allocated_liters, order)} /{' '}
                    {volumeCell(progress.ordered_liters, order)} {uniteCourte}
                  </span>
                  <span
                    className={cn(
                      'shrink-0 font-semibold',
                      progress.to_allocate_liters > 0 ? 'text-flame-ink' : 'text-muted-foreground',
                    )}
                  >
                    Reste {volumeCell(progress.to_allocate_liters, order)} {uniteCourte}
                  </span>
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
